import React, { useState, useEffect } from 'react';
import { getPolymarketEvents } from '../../services/api';
import './PolymarketBuzz.css';

const PolymarketLogo = ({ size = 20 }) => (
  <svg viewBox="0 0 136 168" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: size, height: size }}>
    <path d="M136.267 152.495C136.267 159.76 136.267 163.392 133.891 165.192C131.516 166.993 128.019 166.012 121.024 164.049L8.63192 132.51C4.41793 131.328 2.31093 130.737 1.09248 129.129C-0.125977 127.522 -0.125977 125.333 -0.125977 120.957V47.0434C-0.125977 42.6667 -0.125977 40.4783 1.09248 38.8709C2.31093 37.2634 4.41792 36.6722 8.63191 35.4897L121.024 3.95096C128.019 1.98834 131.516 1.00703 133.891 2.80771C136.267 4.60839 136.267 8.24049 136.267 15.5047V152.495ZM27.9043 122.228L120.966 148.345V96.1133L27.9043 122.228ZM15.1738 110.111L108.217 84L15.1738 57.8887V110.111ZM27.9033 45.7725L120.966 71.8877V19.6553L27.9033 45.7725Z" fill="currentColor"/>
  </svg>
);

const MAX_TAGS = 4;
const MAX_EVENTS_PER_TAG = 5;

const PREFERRED_TAG_ORDER = ['economy', 'fed-rates', 'stocks', 'crypto', 'politics'];

const formatVolume = (vol) => {
  if (!vol || vol <= 0) return '-';
  if (vol >= 1_000_000) return `$${(vol / 1_000_000).toFixed(1)}M`;
  if (vol >= 1_000) return `$${(vol / 1_000).toFixed(0)}K`;
  return `$${Math.round(vol)}`;
};

const parseOutcomePrices = (market) => {
  try {
    if (!market.outcomePrices) return null;
    const prices = JSON.parse(market.outcomePrices);
    const yes = parseFloat(prices[0]);
    const no = parseFloat(prices[1]);
    if (isNaN(yes) || isNaN(no)) return null;
    return { yes, no };
  } catch {
    return null;
  }
};

const getActiveMarket = (markets) => {
  if (!markets || markets.length === 0) return null;
  return markets.find(
    (m) => m.active === true && m.closed === false && m.acceptingOrders === true
  ) || null;
};

const EventCard = ({ event }) => {
  const market = getActiveMarket(event.markets);
  if (!market) return null;

  const probs = parseOutcomePrices(market);
  if (!probs) return null;

  const yesPercent = Math.round(probs.yes * 100);
  const noPercent = Math.round(probs.no * 100);
  const volume = market.volume24hr > 0 ? market.volume24hr : event.volume24hr;
  const slug = event.slug || event.id;

  const handleClick = () => {
    window.open(`https://polymarket.com/event/${slug}`, '_blank');
  };

  return (
    <div className="pmb-card" onClick={handleClick}>
      <p className="pmb-title">{event.title}</p>

      <div className="pmb-prob-labels">
        <span className="pmb-prob-yes">{yesPercent}%</span>
        <span className="pmb-prob-no">{noPercent}%</span>
      </div>

      <div className="pmb-bar-wrap">
        <div className="pmb-bar-yes" style={{ width: `${yesPercent}%` }} />
        <div className="pmb-bar-no" style={{ width: `${noPercent}%` }} />
      </div>

      <div className="pmb-footer">
        <span className="pmb-label-yes">Yes</span>
        <span className="pmb-label-no">No</span>
        <span className="pmb-volume">24h {formatVolume(volume)}</span>
      </div>
    </div>
  );
};

const SkeletonCards = () => (
  <div className="pmb-scroll">
    {[0, 1, 2].map((i) => (
      <div key={i} className="pmb-card pmb-skeleton" />
    ))}
  </div>
);

const PolymarketBuzz = () => {
  const [tagGroups, setTagGroups] = useState([]);
  const [activeTag, setActiveTag] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPolymarketEvents();
        if (cancelled) return;
        if (!data || !data.list) {
          setLoading(false);
          return;
        }

        // 태그 정렬: 선호 순서 우선
        const sorted = [...data.list].sort((a, b) => {
          const ai = PREFERRED_TAG_ORDER.indexOf(a.tag?.slug);
          const bi = PREFERRED_TAG_ORDER.indexOf(b.tag?.slug);
          const aIdx = ai === -1 ? 999 : ai;
          const bIdx = bi === -1 ? 999 : bi;
          return aIdx - bIdx;
        });

        const groups = sorted
          .slice(0, MAX_TAGS)
          .map((group) => ({
            tag: group.tag,
            events: (group.events || [])
              .filter((ev) => {
                if (!ev.volume24hr || ev.volume24hr <= 0) return false;
                const market = getActiveMarket(ev.markets);
                if (!market) return false;
                const probs = parseOutcomePrices(market);
                return probs !== null;
              })
              .slice(0, MAX_EVENTS_PER_TAG),
          }))
          .filter((g) => g.events.length > 0);

        setTagGroups(groups);
        if (groups.length > 0) setActiveTag(groups[0].tag?.slug);
      } catch (err) {
        console.error('[PolymarketBuzz] fetch error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const activeGroup = tagGroups.find((g) => g.tag?.slug === activeTag);

  return (
    <div className="feature-section">
      <div className="flex items-center gap-2 mb-3">
        <div className="feature-icon pmb-icon">
          <PolymarketLogo size={20} />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-bold text-black">Polymarket 예측</h2>
            <span className="edu-fun-badge">스낵 콘텐츠</span>
          </div>
          <p className="text-xs text-gray-400 mt-0.5">실제 돈이 베팅된 시장 확률</p>
        </div>
      </div>
      <p className="text-xs text-gray-600 pl-1 mb-3">Yes/No 확률은 시장 참여자들의 집단 예측을 반영하며, 거래량이 높을수록 신뢰도가 높습니다.</p>

      {loading ? (
        <SkeletonCards />
      ) : tagGroups.length === 0 ? null : (
        <>
          {tagGroups.length > 1 && (
            <div className="pmb-tabs">
              {tagGroups.map((g) => (
                <button
                  key={g.tag?.slug}
                  className={`pmb-tab ${activeTag === g.tag?.slug ? 'pmb-tab-active' : ''}`}
                  onClick={() => setActiveTag(g.tag?.slug)}
                >
                  {g.tag?.label || g.tag?.slug}
                </button>
              ))}
            </div>
          )}

          <div className="pmb-scroll">
            {(activeGroup?.events || []).map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default PolymarketBuzz;
